import {CardContent, Grid, InputAdornment, Stack} from "@mui/material";
import {Checkbox, IntegerNumberFormat, NumberField, TextField} from "@variocube/app-ui";
import React from "react";
import {LabeledText} from "../../../../../component/labeled-text";
import {ExpenseInvoiceFile} from "../../types";
import {StepProps} from "./step-props";
import {WizardStepCard} from "./wizard-step-card";

interface CarStepProps extends StepProps {
	companyCar?: boolean;
}

export function CarStep(
	{active, completed, invoice, onChange, companyCar = true}: Readonly<CarStepProps>,
) {
	function change(change: Partial<ExpenseInvoiceFile["car"]>) {
		onChange({car: {...invoice.car, ...change}});
	}

	const isRefueledLiter = Boolean(
		invoice?.businessCases?.some(businessCase => businessCase.name?.toLowerCase().includes("betankung")),
	);

	// Only company cars (Dienstfahrzeuge) are guaranteed to be registered in Vienna
	// (license plate prefix "W"). Input-tax-deductible expenses can also apply to
	// rented cars, which may have any Austrian plate — no prefix restriction there.
	const licensePlateLabel = companyCar ? "Kennzeichen Dienstfahrzeug" : "Kennzeichen";

	return (
		<WizardStepCard
			active={active}
			completed={completed}
			title="KFZ"
			activeChildren={
				<CardContent>
					<Grid container spacing={2}>
						<Grid item xs={12} sm={6}>
							<TextField
								label={licensePlateLabel}
								required
								fullWidth
								value={invoice.car?.licensePlate ?? ""}
								onChange={licensePlate => change({licensePlate})}
								inputProps={companyCar ? {pattern: "W-?[A-Za-z0-9]+"} : undefined}
								helperText={companyCar && invoice.car?.licensePlate
									&& !invoice.car?.licensePlate?.startsWith("W")
									&& "Ungültiges Kennzeichen. Firmen-KFZ sind in Wien zugelassen."}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<TextField
								label="KFZ-Typ/Modell"
								fullWidth
								value={invoice.car?.carType ?? ""}
								onChange={carType => change({carType})}
							/>
						</Grid>
						<Grid item xs={12} sm={6}>
							<NumberField
								label="Kilometerstand"
								required
								fullWidth
								numberFormat={IntegerNumberFormat}
								numberValue={invoice.car?.mileageKm ?? null}
								onChangeNumber={mileageKm => change({mileageKm})}
								InputProps={{
									endAdornment: <InputAdornment position="end">km</InputAdornment>,
								}}
							/>
						</Grid>
						{isRefueledLiter && (
							<Grid item xs={12} sm={6}>
								<NumberField
									label="Betankung (Liter)"
									fullWidth
									required={isRefueledLiter}
									numberFormat={IntegerNumberFormat}
									numberValue={invoice.car?.refuelLiter ?? null}
									onChangeNumber={refuelLiter => change({refuelLiter})}
									InputProps={{
										endAdornment: <InputAdornment position="end">l</InputAdornment>,
									}}
								/>
							</Grid>
						)}
					</Grid>
				</CardContent>
			}
			completedChildren={
				<CardContent>
					<Stack gap={2} direction="row" flexWrap="wrap">
						<LabeledText label={licensePlateLabel} text={invoice.car?.licensePlate}/>
						{invoice.car?.carType && <LabeledText label="KFZ-Typ/Modell" text={invoice.car?.carType}/>}
						{invoice.car?.mileageKm && <LabeledText label="Kilometerstand" text={invoice.car?.mileageKm}/>}
						{invoice.car?.refuelLiter && <LabeledText label="Betankung" text={invoice.car?.refuelLiter}/>}
					</Stack>
				</CardContent>
			}
		/>
	);
}
